import { useEffect, useState, useRef } from "react";
import { AiOutlineArrowUp } from "react-icons/ai";
import { sendAiRequest } from "api/aiChat";
import { useAiChat } from "hooks/AiChatContext";
import { getExtractedPath } from "utilities/extractedPath";
import extractUUIDs from "components/layout/utils/extractUUIDs";
import TypingIndicator from "./TypingIndicator";
import ReactMarkdown from "react-markdown";

const MarkdownMessage = ({ text }: { text: string }) => {
  const formattedText = text.replace(/\\n/g, '\n');
  return (
    <div className="prose prose-sm text-white max-w-none">
      <ReactMarkdown>{formattedText}</ReactMarkdown>
    </div>
  );
};
const AiChat = ({ setIsMovementDisabled }: { setIsMovementDisabled: React.Dispatch<React.SetStateAction<number>> }) => {
  const baseUrl = getExtractedPath();
  const { currOrg, curSol } = extractUUIDs(baseUrl);
  const [request, setRequest] = useState<string>('');
  const [reply, setReply] = useState<string>('');
  const userId = crypto.randomUUID();
  const { setCommand, messageHistory, setMessageHistory } = useAiChat();
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const handleComand = (command: any) => {
    setCommand(command);
  };

  const handleRequestSubmit = (e: any) => {
    e.preventDefault();
    setMessageHistory((prev) => [...prev, [request, '__typing__']]);
    setRequest('');

    const sendDataToIa = async () => {
      const response = await sendAiRequest({ user_message: request.toString(), user_id: userId, oid: currOrg, sid: curSol });

      if (response.status === 200) {
        setMessageHistory((prev) => {
          const updated = [...prev];
          const lastItem = updated[updated.length - 1];
          updated[updated.length - 1] = [lastItem[0], response.data.reply];
          return updated;
        });
        if (response.data.command) {
          handleComand(response.data.command);
        }
      } else {
        setMessageHistory((prev) => {
          const updated = [...prev];
          const lastItem = updated[updated.length - 1];
          updated[updated.length - 1] = [lastItem[0], response.data.detail];
          return updated;
        });
      }
    };

    sendDataToIa();
  };


  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messageHistory]);

  const MessageHistory = () => {
    return (
      <>
        {messageHistory.map((item, index) => (
          <div className="w-full flex gap-2 flex-wrap" key={index}>
            <div className="w-full flex items-end justify-end">
              <p className="max-w-[70%] bg-[#303030] p-4 rounded-xl">{item[0]}</p>
            </div>
            <div className="w-full items-start">
              <p className="max-w-[80%] w-max p-4 bg-[#606060] rounded-xl">
                {item[1] === '__typing__' ? (
                  <TypingIndicator />
                ) : (
                  <MarkdownMessage text={item[1]} />
                )}
              </p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </>
    );
  };

  const setBlockUnity = (state: number) => {
    setIsMovementDisabled(state);
  };

  return (
    <div className="w-full h-full">
      <div className="w-full h-full flex flex-col max-h-full">
        <div className="flex-grow p-4 overflow-y-auto flex flex-col gap-2">
          <MessageHistory />
        </div>
        <div className="h-max bg-gray-300 px-4 pt-2 pb-4 min-h-24">
          <form
            className="relative h-full pb-6 bg-[#303030] outline outline-1 outline-[#424242]/70 rounded-3xl"
            onSubmit={handleRequestSubmit}
          >
            <textarea
              onInput={(e: any) => {
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
                setRequest(e.target.value);
              }}
              onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleRequestSubmit(e);
                }
              }}
              onFocus={() => setBlockUnity(1)}
              onBlur={() => setBlockUnity(0)}
              rows={1}
              value={request}
              placeholder="Ask anything"
              className="w-full resize-none bg-transparent overflow-hidden focus:outline-none py-2 px-4 max-h-[200px] overflow-y-auto"
            />
            <button className="absolute bottom-1.5 right-1.5 bg-white rounded-full p-1.5" type="submit">
              <AiOutlineArrowUp size={20} className="text-black" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AiChat;